import type { ItemCategory } from "@/api/types";

type ItemArtMode = "exact" | "alias" | "placeholder";

interface ItemArtResolution {
    src: string;
    mode: ItemArtMode;
}

const EXACT_ITEM_ART_IDS = new Set([
    "accessory.borrowed-signet",
    "accessory.broker-seal",
    "accessory.charm-earring",
    "accessory.gambler-dice",
    "accessory.moon-thread",
    "accessory.moonlit-ring",
    "accessory.ninth-card",
    "accessory.revel-mask",
    "accessory.reliquary-ring",
    "accessory.stage-brooch",
    "accessory.tactician-sash",
    "accessory.track-goggles",
    "accessory.clockwork-cicada",
    "accessory.votive-lens",
    "armor.cathedral-mail",
    "armor.chain-coat",
    "armor.court-doublet",
    "armor.frost-steel-plate",
    "armor.leather-vest",
    "armor.pilgrim-mantle",
    "armor.rail-leathers",
    "armor.riveted-apron",
    "armor.scout-cloak",
    "armor.silk-robe",
    "armor.undertow-coat",
    "armor.woven-mantle",
    "axe.wake-cleaver",
    "bow.long-bow",
    "bow.rainfeather",
    "charm.faith-relic",
    "charm.heartfire-token",
    "charm.house-sigil",
    "charm.lucky-thread",
    "charm.market-ledger",
    "charm.storm-ticket",
    "charm.warm-ember-knot",
    "bow.gloamstring",
    "boots.silent-step",
    "boots.travelers-tread",
    "boots.relay-greaves",
    "charm.thorn-circlet-shard",
    "charm.old-map-heart",
    "consumable.antidote-brew",
    "consumable.apothecary-tonic",
    "consumable.bandage-roll",
    "consumable.focus-incense",
    "consumable.forge-broth",
    "consumable.field-ration",
    "consumable.hearty-meal",
    "consumable.health-potion",
    "consumable.mana-potion",
    "consumable.moon-tea",
    "consumable.night-ink",
    "consumable.phoenix-elixir",
    "consumable.smoke-vial",
    "consumable.storm-fuse",
    "consumable.stage-salt",
    "consumable.sweet-bun",
    "dagger.stage-stiletto",
    "dagger.shadowed-fang",
    "focus.grimoire-dusted",
    "focus.orb-tidesong",
    "grimoire.hush-atlas",
    "helm.echo-visor",
    "helm.frost-visor",
    "helm.iron-circlet",
    "helm.leather-cap",
    "helm.scholars-hood",
    "key.backstage-pass",
    "key.audience-writ",
    "key.black-vault-key",
    "key.choir-mark",
    "key.patron-token",
    "key.relief-route-lantern",
    "key.route-seal",
    "material.beast-pelt",
    "material.bronze-gear",
    "material.cathedral-glass",
    "material.ember-resin",
    "material.fallstar-fragment",
    "material.holy-salt",
    "material.mana-crystal",
    "material.mercury-ink",
    "material.rail-spark-plug",
    "material.silvered-thread",
    "material.starforged-ingot",
    "material.stage-silk",
    "pistol.apprentice-revolver",
    "pistol.storm-caliber",
    "pistol.hex-revolver",
    "shield.buckler-chased",
    "shield.sainted-splinter",
    "shield.tower-bulwark",
    "staff.apprentice-wand",
    "staff.cathedral-censer",
    "staff.moss-scepter",
    "staff.starlit-focus",
    "sword.sponsor-rapier",
    "sword.training-blade",
    "sword.duelist-saber",
    "sword.oath-blade",
    "sword.unknown-blade",
]);

const ITEM_ART_ALIASES: Record<string, string> = {
};

export function resolveItemArt(itemId: string, category: ItemCategory): ItemArtResolution {
    if (EXACT_ITEM_ART_IDS.has(itemId)) {
        return { src: `/art/items/${itemId}.png`, mode: "exact" };
    }

    const aliased = ITEM_ART_ALIASES[itemId];
    if (aliased && EXACT_ITEM_ART_IDS.has(aliased)) {
        return { src: `/art/items/${aliased}.png`, mode: "alias" };
    }

    return { src: `/art/items/_placeholder-${category}.png`, mode: "placeholder" };
}

export function isPlaceholderItemArt(itemId: string, category: ItemCategory): boolean {
    return resolveItemArt(itemId, category).mode === "placeholder";
}
